import { getIconComponent } from '@/components/modals/IconPicker';
import { useFieldConfig } from '@/hooks/useFieldConfig';

interface HierarchyTabProps {
  labelDraft: { l1: string; l2: string; l3: string };
  labelSaving: boolean;
  labelSaved: boolean;
  labelSaveError: string;
  onLabelChange: (key: 'l1' | 'l2' | 'l3', value: string) => void;
  onSaveLabels: () => void;
  products: any[];
  allSubProducts: any[];
  onAddProduct: () => void;
  onEditProduct: (product: any) => void;
  onAddSubProduct: () => void;
  onEditSubProduct: (subProduct: any) => void;
}

export function HierarchyTab({
  labelDraft,
  labelSaving,
  labelSaved,
  labelSaveError,
  onLabelChange,
  onSaveLabels,
  products,
  allSubProducts,
  onAddProduct,
  onEditProduct,
  onAddSubProduct,
  onEditSubProduct,
}: HierarchyTabProps) {
  const fc = useFieldConfig();

  const subProductsByProduct = products.map((product) => ({
    ...product,
    subProducts: allSubProducts.filter((sp) => sp.productId === product.id),
  }));

  const LEVEL_HINTS = [
    { key: 'l1' as const, hint: 'e.g. Product, Function, Division' },
    { key: 'l2' as const, hint: 'e.g. Sub-Product, Group, Team' },
    { key: 'l3' as const, hint: 'e.g. Initiative, Project, Epic' },
  ];

  return (
    <div className="max-w-3xl space-y-8">
      {/* Section 1: Label editor */}
      <div>
        <h2 className="text-base font-semibold text-text-primary mb-1">Hierarchy Labels</h2>
        <p className="text-sm text-text-secondary mb-4">
          Rename each level to match your organisation's terminology. Changes apply across the entire app.
        </p>

        <div className="grid grid-cols-3 gap-4 mb-4">
          {LEVEL_HINTS.map(({ key, hint }, i) => (
            <div key={key}>
              <label className="block text-xs font-medium text-text-secondary mb-1">
                Level {i + 1}
              </label>
              <input
                type="text"
                value={labelDraft[key]}
                onChange={(e) => onLabelChange(key, e.target.value)}
                placeholder={hint}
                maxLength={30}
                className="w-full rounded-md border border-border-default bg-background-primary text-sm text-text-primary px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="text-xs text-text-tertiary mt-1">{hint}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onSaveLabels}
            disabled={labelSaving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {labelSaving ? 'Saving...' : 'Save Labels'}
          </button>
          {labelSaved && <span className="text-sm text-green-600">✓ Labels saved</span>}
          {labelSaveError && <span className="text-sm text-red-600">{labelSaveError}</span>}
        </div>
      </div>

      {/* Section 2: Products / sub-products tree */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-text-primary uppercase tracking-wide">
            {fc.label('product')}s
          </h2>
          <div className="flex gap-2">
            <button
              onClick={onAddSubProduct}
              className="px-3 py-1.5 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700"
            >
              + Add {fc.label('subProduct')}
            </button>
            <button
              onClick={onAddProduct}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              + Add {fc.label('product')}
            </button>
          </div>
        </div>

        <div className="border border-border-default rounded-md overflow-hidden">
          {subProductsByProduct.length === 0 ? (
            <p className="py-6 text-center text-sm text-text-secondary">
              No {fc.label('product').toLowerCase()}s yet. Click &quot;+ Add {fc.label('product')}&quot; to create one.
            </p>
          ) : (
            subProductsByProduct.map((product, pIdx) => {
              const IconComponent = getIconComponent(product.icon);
              const isLast = pIdx === subProductsByProduct.length - 1;
              return (
                <div key={product.id} className={!isLast ? 'border-b border-border-default' : ''}>
                  {/* L1 row */}
                  <div className="flex items-center gap-3 px-4 py-2.5 bg-surface-subtle hover:bg-surface-hover">
                    <div
                      className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: product.iconColor + '20' }}
                    >
                      {IconComponent && (
                        <div style={{ color: product.iconColor }}>
                          <IconComponent className="h-3.5 w-3.5" />
                        </div>
                      )}
                    </div>
                    <span className="text-sm font-semibold text-text-primary flex-1">{product.name}</span>
                    <span className="text-xs text-text-tertiary whitespace-nowrap mr-4">
                      {product.subProducts.length}{' '}
                      {product.subProducts.length === 1 ? fc.label('subProduct') : `${fc.label('subProduct')}s`}
                    </span>
                    <button
                      onClick={() => onEditProduct(product)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Edit
                    </button>
                  </div>

                  {/* L2 rows */}
                  {product.subProducts.map((sp: any, spIdx: number) => {
                    const isLastSp = spIdx === product.subProducts.length - 1;
                    return (
                      <div
                        key={sp.id}
                        className="flex items-center gap-3 px-4 py-2 border-t border-gray-100 hover:bg-surface-hover"
                      >
                        <span className="text-text-tertiary text-xs w-6 text-right flex-shrink-0 select-none">
                          {isLastSp ? '└' : '├'}
                        </span>
                        <span className="text-sm text-text-primary flex-1">{sp.name}</span>
                        <span className="text-xs text-text-tertiary whitespace-nowrap mr-4">
                          {sp._count?.initiatives ?? 0}{' '}
                          {(sp._count?.initiatives ?? 0) === 1
                            ? fc.label('initiative')
                            : `${fc.label('initiative')}s`}
                        </span>
                        <button
                          onClick={() => onEditSubProduct(sp)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Edit
                        </button>
                      </div>
                    );
                  })}

                  {product.subProducts.length === 0 && (
                    <div className="px-4 py-2 border-t border-gray-100">
                      <span className="text-xs text-text-tertiary italic">
                        No {fc.label('subProduct').toLowerCase()}s yet
                      </span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
